<script setup lang="ts">
import {
  computed,
  ref,
} from 'vue';

import type { IApiFormErrors } from '@modules/core/services/query/types';
import type {
  FormSchema,
  VFormRef,
} from '@modules/forms/components/VForm/types';
import type {
  ITFAGenerateResponse,
  ITFASchema,
} from '@modules/profile-settings/submodules/two-factor-auth/types';
import VLoader from '@components/loader/VLoader.vue';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { EFormInputTypes } from '@modules/forms/enums';
import { useI18n } from '@modules/i18n/composables';
import { useCurrentNamespace } from '@modules/permissions/composables';
import {
  TRANSLATION_GROUP_ADMIN_PROFILE_SETTINGS,
  TRANSLATION_GROUP_AFFILIATE_PROFILE_SETTINGS,
} from '@modules/profile-settings/constants';
import {
  useAdminProfileQuery,
  useAffiliateProfileQuery,
} from '@modules/profile-settings/queries';
import {
  useProfileSettingsTFAClearMutation,
  useProfileSettingsTFAConfirmMutation,
  useProfileSettingsTFAGenerateMutation,
} from '@modules/profile-settings/submodules/two-factor-auth/mutations';
import { useToastStore } from '@modules/toast/store';
import { useUserSupervision } from '@modules/user/composables';

const { isAdminNamespace } = useCurrentNamespace();
const { isSupervision } = useUserSupervision();
const { getTranslation } = useI18n();
const { displayPresetToast } = useToastStore();

const { data: profileAdmin, isLoading: isLoadingAdminData, setData: setAdminData } = useAdminProfileQuery();
const { data: profileAffiliate, isLoading: isLoadingAffiliate, setData: setAffiliateData } = useAffiliateProfileQuery();
const {
  mutateAsync: generateTfa,
  isLoading: isLoadingGenerate,
  errors: errorsGenerate,
  clearError: clearErrorGenerate,
} = useProfileSettingsTFAGenerateMutation();
const {
  mutateAsync: confirmTfa,
  isLoading: isLoadingConfirm,
  errors: errorsConfirm,
  clearError: clearErrorConfirm,
} = useProfileSettingsTFAConfirmMutation();
const {
  mutateAsync: clearTfa,
  isLoading: isLoadingClear,
  errors: errorsClear,
  clearError: clearErrorClear,
} = useProfileSettingsTFAClearMutation();

const translationGroup = computed(() => (
  isAdminNamespace.value ? TRANSLATION_GROUP_ADMIN_PROFILE_SETTINGS : TRANSLATION_GROUP_AFFILIATE_PROFILE_SETTINGS
));

const profile = computed(() => (
  isAdminNamespace.value ? profileAdmin.value : profileAffiliate.value
));

const isLoading = computed(() => (
  isAdminNamespace.value ? isLoadingAdminData.value : isLoadingAffiliate.value
));

const generateData = ref<ITFAGenerateResponse>();

const isGenerateStep = computed(() => !profile.value?.mfa_enabled && !generateData.value);
const isConfirmStep = computed(() => !profile.value?.mfa_enabled && generateData.value);
const isDisableStep = computed(() => !!profile.value?.mfa_enabled);

const isLoadingSave = computed(() => (
  isLoadingGenerate.value || isLoadingConfirm.value || isLoadingClear.value
));

const errors = computed<IApiFormErrors | undefined>(() => {
  if (isGenerateStep.value) {
    return errorsGenerate.value;
  }

  if (isConfirmStep.value) {
    return errorsConfirm.value;
  }

  if (isDisableStep.value) {
    return errorsClear.value;
  }

  return undefined;
});

function clearError(key: string): void {
  if (isGenerateStep.value) {
    clearErrorGenerate(key);
    return;
  }

  if (isConfirmStep.value) {
    clearErrorConfirm(key);
    return;
  }

  if (isDisableStep.value) {
    clearErrorClear(key);
  }
}

const schema = computed<FormSchema<ITFASchema>>(() => {
  if (isGenerateStep.value) {
    return {
      password: {
        type: EFormInputTypes.PASSWORD,
        label: getTranslation(translationGroup.value, 'TFA_CURRENT_PASSWORD').value,
        disabled: isSupervision.value,
        required: true,
      },
    };
  }

  if (isConfirmStep.value) {
    return {
      google_secret_value: {
        type: EFormInputTypes.TEXT,
        label: getTranslation(translationGroup.value, 'TFA_SECRET_VALUE').value,
        value: generateData.value?.google_secret_value,
        readonly: true,
      },
      mfa_code: {
        type: EFormInputTypes.TEXT,
        label: getTranslation(translationGroup.value, 'TFA_ENTER_CODE_FOR_ENABLE').value,
        autocomplete: 'off',
        required: true,
      },
      google_qr_uri: {
        type: EFormInputTypes.SLOT,
      },
    };
  }

  if (isDisableStep.value) {
    return {
      mfa_code: {
        type: EFormInputTypes.TEXT,
        label: getTranslation(translationGroup.value, 'TFA_ENTER_CODE_FOR_DISABLE').value,
        autocomplete: 'off',
        required: true,
      },
    };
  }

  return {};
});

function setEnabled(enabled: boolean): void {
  if (isAdminNamespace.value) {
    setAdminData((data) => {
      if (!data) {
        return data;
      }

      return {
        ...data,
        profile: {
          ...data.profile,
          mfa_enabled: enabled,
        },
      };
    });
  } else {
    setAffiliateData((data) => {
      if (!data) {
        return data;
      }

      return {
        ...data,
        profile: {
          ...data.profile,
          mfa_enabled: enabled,
        },
      };
    });
  }
}

const form = ref<VFormRef>();

async function onSubmit(values: ITFASchema): Promise<void> {
  if (isGenerateStep.value) {
    generateData.value = await generateTfa(values);
    form.value?.reset();
    return;
  }

  if (isConfirmStep.value) {
    await confirmTfa(values);
    setEnabled(true);
    generateData.value = undefined;
    form.value?.reset();
    displayPresetToast('success', getTranslation(translationGroup.value, 'TFA_ENABLED').value);
    return;
  }

  if (isDisableStep.value) {
    await clearTfa(values);
    form.value?.reset();
    setEnabled(false);
    displayPresetToast('success', getTranslation(translationGroup.value, 'TFA_DISABLED').value);
  }
}
</script>

<template>
  <VLoader v-data-qa-auto-id="'TwoFactorAuthPage'"
    :loading="isLoading"
    min-height="200px"
  >
    <VForm
      ref="form"
      :schema="schema"
      :submit-btn-label="$getTranslation(translationGroup, 'SAVE')"
      :custom-errors="errors"
      :is-loading="isLoadingSave"
      :readonly="isSupervision"
      @interacted="clearError"
      @submit="onSubmit"
    >
      <template #google_qr_uri>
        <img
          v-if="generateData"
          :src="generateData.google_qr_uri"
          alt="QrCode"
        >
      </template>
    </VForm>
  </VLoader>
</template>
